<script setup lang="ts">
import { computed } from 'vue'
import {
  itemScopeAttributes,
  useModelStrings,
  type DetailTemplateProps, ModelList,
} from '@jantm/model-core'
import type {Offer} from "@/types/Wiser/offer.ts";
import DocumentsComponent from "@/components/DocumentsComponent.vue";
import OfferLinesComponent from "@/components/OfferLinesComponent.vue";

const props = withDefaults(defineProps<DetailTemplateProps<Offer>>(), { headingLevel: 2 })

const { t, core, locale, detailLabel } = useModelStrings(() => props.definition)

const scope = computed(() => itemScopeAttributes(props.definition))
const heading = computed(() => `h${props.headingLevel}` as const)
const regionLabel = computed(() => props.ariaLabel ?? detailLabel.value)
</script>

<template>
  <section
    class="wiser-detail offer-detail"
    role="region"
    :aria-label="regionLabel"
    :aria-busy="loading && Boolean(entity)"
    v-bind="scope"
  >
    <p
      v-if="error"
      class="wiser-detail__error offer-detail__error"
      role="alert"
    >
      {{ core('state.error', { fallback: 'Something went wrong.' }) }}
    </p>
    <p
      v-else-if="loading && !entity"
      role="status"
    >
      {{ core('state.loading', { fallback: 'Loading…' }) }}
    </p>
    <p
      v-else-if="!entity"
      role="status"
    >
      {{ core('state.notFound', { fallback: 'Not found.' }) }}
    </p>

    <template v-else>
      <!--
        The brand and model together are the accessible name of this record, so
        they are the heading — not decoration above it. The heading level comes
        from the caller so the page outline stays intact wherever it is used.
      -->
      <component
        :is="heading"
        class="wiser-detail__title offer-detail__title"
      >
        <span>{{ entity.korteomschrijving }}</span>
        <span class="wiser-detail__badge offer-detail__badge">{{ entity.offertestatus }}</span>
        <a class="btn btn-primary float-right" :href="`/api/wiser/generateOffer/${entity.id}?print=true`" onclick="this.target='_blank'">Print</a>
      </component>

      <div class="wiser-detail__description offer-detail__description">
        <h3>Gegevens</h3>
        <dl class="wiser-detail__field offer-detail__field" v-if="entity.naam">
          <dt>{{ t('model.offer.field.naam', { fallback: 'Naam' }) }}</dt>
          <dd>
            <span
              class="wiser-detail__field offer-detail__field"
              itemprop="identifier"
            >{{ entity.naam }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field offer-detail__field" v-if="entity.contactpersoon">
          <dt>{{ t('model.offer.field.contactpersoon', { fallback: 'Contactpersoon' }) }}</dt>
          <dd>
            <span>{{ entity.contactpersoon }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field offer-detail__field" v-if="entity.adres || entity.adresregel2 || entity.postcode || entity.plaats">
          <dt>{{ t('model.offer.field.adres', { fallback: 'Adres' }) }}</dt>
          <dd>
            <span>{{ entity.adres }} {{ entity.adresregel2 }}, {{ entity.postcode }} {{ entity.plaats }}</span>
            <span v-if="entity.land && 'NL' !== entity.land">[{{ entity.land }}]</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field offer-detail__field" v-if="entity.telefoonnummer">
          <dt>{{ t('model.offer.field.telefoonnummer', { fallback: 'Telefoon' }) }}</dt>
          <dd>
            <span>{{ entity.telefoonnummer }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field offer-detail__field" v-if="entity.offertedatum">
          <dt>{{ t('model.offer.field.offertedatum', { fallback: 'Offertedatum' }) }}</dt>
          <dd>
            <span>{{ entity.offertedatum }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field offer-detail__field" v-if="entity?.referentie">
          <dt>{{ t('model.offer.field.referentie', { fallback: 'Referentie' }) }}</dt>
          <dd>
            <span>{{ entity.referentie }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field offer-detail__field" v-if="entity?.bevestigdop">
          <dt>{{ t('model.offer.field.bevestigdop', { fallback: 'Bevestigd op' }) }}</dt>
          <dd>
            <span>{{ entity.bevestigdop }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field offer-detail__field" v-if="entity?.opvolgdatum">
          <dt>{{ t('model.offer.field.opvolgdatum', { fallback: 'Opvolgdatum' }) }}</dt>
          <dd>
            <span>{{ entity.opvolgdatum }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field offer-detail__field" v-if="entity?.vervaldatum">
          <dt>{{ t('model.offer.field.vervaldatum', { fallback: 'Vervaldatum' }) }}</dt>
          <dd>
            <span>{{ entity.vervaldatum }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field offer-detail__field" v-if="entity?.korteomschrijving">
          <dt>{{ t('model.offer.field.korteomschrijving', { fallback: 'Korteomschrijving' }) }}</dt>
          <dd>
            <span>{{ entity.korteomschrijving }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field offer-detail__field" v-if="entity?.langeomschrijving">
          <dt>{{ t('model.offer.field.langeomschrijving', { fallback: 'Omschrijving' }) }}</dt>
          <dd>
            <span v-html="entity.langeomschrijving" />
          </dd>
        </dl>

        <dl class="wiser-detail__field offer-detail__field" v-if="entity?.langeomschrijving2">
          <dt>{{ t('model.offer.field.langeomschrijving2', { fallback: 'Omschrijving 2' }) }}</dt>
          <dd>
            <span v-html="entity.langeomschrijving2" />
          </dd>
        </dl>

        <dl class="wiser-detail__field offer-detail__field" v-if="entity?.added_on">
          <dt>{{ t('model.offer.field.added_on', { fallback: 'Toevoegingsdatum' }) }}</dt>
          <dd>
            <span v-html="entity.added_on" />
          </dd>
        </dl>

        <dl class="wiser-detail__field offer-detail__field" v-if="entity?.changed_on">
          <dt>{{ t('model.offer.field.changed_on', { fallback: 'Wijzigingsdatum' }) }}</dt>
          <dd>
            <span v-html="entity.changed_on" />
          </dd>
        </dl>
      </div>

      <OfferLinesComponent class="wiser-detail__description relation-detail__description" v-if="entity.offerteregelonderofferte?.length ?? 0 > 0" :entityId="entity.id" />

      <DocumentsComponent :entityId="entity.id" />
    </template>
  </section>
</template>
